import { computed, inject, Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { toObservable } from '@angular/core/rxjs-interop';

import { DateTime } from 'luxon';
import { filter, map, Observable, take, tap } from 'rxjs';

import { ENVIRONMENT, Environment } from '@hmax-order/frontend/environment';
import { apiResource, UserStore } from '@hmax-order/frontend/state';
import { DateUtils } from '@hmax-order/shared/date';
import { ALERT_SOUND_SLOTS, AlertSoundId, AlertSoundSlot, Cfg, CfgName, resolveAlertSound } from '@hmax-order/shared/types';

/** Normaliza o nome da cfg para chave de índice: sem underscore, minúsculo. */
function normalizeName(name: string): string {
  return name.split('_').join('').toLowerCase();
}

function toCfgValue(value: unknown): string {
  // Cfg vazia (ex.: coordenada de geo não preenchida) grava string vazia, nunca "null"/"undefined".
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'boolean') {
    return value ? '1' : '0';
  }

  return String(value);
}

@Injectable({ providedIn: 'root' })
export class CfgService {
  private readonly _env: Environment = inject(ENVIRONMENT);
  private readonly _http = inject(HttpClient);
  private readonly _injector = inject(Injector);

  private readonly _userStore = inject(UserStore);

  private readonly _resource = apiResource<Cfg[]>(
    // Ocioso enquanto não há empresa atual: a API valida `companyId` do token e
    // devolve 500 sem ele. Depender do signal também refaz o fetch sozinho na
    // troca de empresa — isolamento entre tenants pelo próprio recurso.
    () => (this._userStore.getCurrentCompanyId() ? { path: 'cfgs', params: { invalidateCache: 1 } } : undefined),
    { defaultValue: [] },
  );

  /**
   * Configurações da empresa atual.
   *
   * Passa por `hasValue()` porque ler `value()` de um resource em erro **lança**,
   * mesmo com `defaultValue` definido. Sem essa guarda, uma API fora do ar
   * derrubaria todo componente que lê configuração.
   */
  readonly cfgs = computed<Cfg[]>(() => (this._resource.hasValue() ? this._resource.value() : []));
  readonly isLoading = this._resource.isLoading;
  readonly error = this._resource.error;

  /**
   * Configurações indexadas pelo nome normalizado.
   *
   * Substitui o selector `normalizeAndMapConfigs` do NGXS — mesma saída, mas
   * recalculada só quando a lista muda.
   */
  readonly byName = computed<Record<string, Cfg>>(() => {
    const indexed: Record<string, Cfg> = {};

    for (const cfg of this.cfgs()) {
      if (cfg?.name) {
        indexed[normalizeName(cfg.name.toString())] = cfg;
      }
    }

    return indexed;
  });

  private get _url(): string {
    return `${this._env.apiUrl}/cfgs`;
  }

  /**
   * M2-D68 — o som que a EMPRESA escolheu para o tipo de aviso, ou o padrão do tipo enquanto a cfg não chegou
   * ou se o valor gravado não for um som conhecido. Lido dentro de `computed`/efeito, acompanha a troca de
   * empresa e a gravação em Configurações sem recarregar a tela.
   */
  alertSound(slot: AlertSoundSlot): AlertSoundId {
    const cfgName = ALERT_SOUND_SLOTS.find((definition) => definition.slot === slot)?.cfgName;
    return resolveAlertSound(slot, cfgName ? this.byName()[normalizeName(cfgName)]?.value : undefined);
  }

  /**
   * Emite uma vez quando as configurações terminam de carregar.
   *
   * Existe para os resolvers de rota, que precisam segurar a navegação até o
   * dado chegar. Fora de rota, prefira ler o signal `cfgs` direto — ele já é
   * reativo e não precisa de subscribe.
   *
   * Erro não rejeita: a navegação não deve travar por causa de configuração.
   * Nesse caso emite a lista vazia e `error()` fica preenchido.
   */
  getAll(): Observable<Cfg[]> {
    return toObservable(this._resource.status, { injector: this._injector }).pipe(
      // `idle` também encerra: recurso ocioso (sem empresa atual) nunca vai carregar,
      // e sem ele aqui o resolver que espera esta emissão pendura a navegação para sempre.
      filter((status) => status === 'resolved' || status === 'error' || status === 'local' || status === 'idle'),
      take(1),
      map(() => this.cfgs()),
    );
  }

  /** Refaz o fetch. Usar após trocar de empresa ou salvar alterações. */
  reload(): void {
    this._resource.reload();
  }

  /**
   * Retornar String com valor atual da configuração
   * @param name Nome da configuração
   */
  getString(name: CfgName): string | undefined {
    return this.cfgs().find((cfg: Cfg) => cfg?.name === name)?.value || undefined;
  }

  /**
   * Retornar CFGs com base no array de CFGs informado
   * @param names Array contendo nomes de configuração
   */
  getByCfgNames(names: CfgName[]): Cfg[] {
    return this.cfgs().filter((cfg: Cfg) => cfg?.name && names.includes(cfg.name));
  }

  /**
   * Retornar DateTime com valor atual da configuração
   */
  getDateTime(name: CfgName): DateTime {
    return DateUtils.toDateTime(this.getString(name));
  }

  /**
   * Retornar Boolean com valor atual da configuração
   */
  getBoolean(name: CfgName): boolean {
    return Boolean(Number(this.getString(name)));
  }

  /**
   * Atualizar configurações do hotel
   * @param cfg Configurações a serem atualizadas
   */
  update(cfg: Cfg[]): Observable<unknown> {
    const updateData = cfg
      .filter((item) => Boolean(item?.id) || Boolean(item?.name))
      .map((item) => ({ id: item.id, name: item.name, value: item.value }));

    return this._http.post(`${this._url}/update-many`, updateData).pipe(tap(() => this.reload()));
  }

  /**
   * Verificar se a configuração deve ser exibida para o usuário
   * @param option Nome da configuração
   */
  shouldShowOptions(option: string, cfgs: { [key: string]: Cfg }, superUser: boolean): boolean {
    if (!cfgs[option.toLowerCase()]?.superUser) {
      return true;
    }

    return Boolean(superUser);
  }

  /**
   * Transformar opções do formulário em cfgs para envio ao back-end
   */
  formToCfgs(form: Record<string, unknown>, cfgs: { [key: string]: Cfg }, superUser: boolean): Cfg[] {
    const updateCfgs: Cfg[] = [];

    Object.keys(form).forEach((data: string) => {
      const currencyCfg = cfgs[data.toLowerCase()];
      const option = currencyCfg?.name;

      if (currencyCfg?.superUser && !superUser) {
        return;
      }

      const value = toCfgValue(form[data]);

      if (option != null && currencyCfg?.value !== value) {
        updateCfgs.push({
          companyId: '',
          id: currencyCfg?.id,
          name: option,
          value,
        });
      }
    });

    return updateCfgs;
  }
}
